import React, { useEffect, useState } from "react";
import { Modal, Button, Confirm } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Application } from "../applications/types";
import StudentApplicationList from "../applications/components/StudentApplicationList";
import { ApplicationStatus } from "../../app/enums/applicationStatus";
import agent from "../../app/api/agent";
import useToaster from "../../app/hooks/useToaster";
import { Internship } from "./types";

interface IProps {
  internship: Internship;
  approved: boolean;
  onUpdate?: () => void;
}

const InternshipApplicationsModal: React.FC<IProps> = ({ internship, approved, onUpdate }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { success } = useToaster();

  const [pendingApplications, setPendingApplications] = useState<Application[]>([]);
  const [approvedApplications, setApprovedApplications] = useState<Application[]>([]);
  const [studentCancelledApplications, setStudentCancelledApplications] = useState<Application[]>([]);
  const [pendingApprovalId, setPendingApprovalId] = useState<number | null>(null);

  const doApprove = (applicationId: number, isApproved: boolean) => {
    const approvedApplication = pendingApplications.find((x) => x.id === applicationId);
    if (!approvedApplication) return;

    agent.Applications.approve(applicationId, isApproved).then(() => {
      if (isApproved) {
        setApprovedApplications([...approvedApplications, approvedApplication]);
        setPendingApplications(pendingApplications.filter((a) => a.id !== applicationId));
        success(t("applications.notifications.application_confirmed"));
        onUpdate?.();
      } else {
        setPendingApplications(pendingApplications.filter((a) => a.id !== applicationId));
        success(t("applications.notifications.application_rejected"));
      }
    });
  };

  const handleApplicationConfirmation = (applicationId: number, isApproved: boolean) => {
    const approvedApplication = pendingApplications.find((x) => x.id === applicationId);
    if (!approvedApplication) return;

    if (isApproved) {
      const isLastSpot = approvedApplications.length === internship.participantsCapacity - 1;
      if (isLastSpot) {
        setPendingApprovalId(applicationId);
        return;
      }
    }

    doApprove(applicationId, isApproved);
  };

  const handleApplicationCancellation = (applicationId: number) => {
    agent.Applications.cancel(applicationId).then(() => {
      setApprovedApplications(approvedApplications.filter((a) => a.id !== applicationId));
      success(t("applications.notifications.application_cancelled"));
    });
  };

  const applications = approved
    ? approvedApplications.concat(studentCancelledApplications)
    : pendingApplications.concat(studentCancelledApplications);

  useEffect(() => {
    if (!internship.applications) return;

    setPendingApplications(
      internship.applications.filter((a) =>
        [ApplicationStatus.WAITING_FOR_COMPANY_APPROVAL, ApplicationStatus.WAITING_FOR_SCHOOL_APPROVAL].includes(
          a.status
        )
      )
    );
    setApprovedApplications(internship.applications.filter((a) => a.status === ApplicationStatus.COMPANY_APPROVED));
    setStudentCancelledApplications(
      internship.applications.filter((a) => a.status === ApplicationStatus.STUDENT_CANCELLED)
    );
  }, [internship]);

  const renderModalTrigger = () => {
    if (approved) {
      if (approvedApplications.length === 0) return null;

      return (
        <Link to="#">
          {t("common.student_applications")}: {approvedApplications.length}
        </Link>
      );
    } else {
      if (pendingApplications.length === 0) return null;

      return (
        <Button primary>
          {t("applications.company.see_new_applications")} ({pendingApplications.length})
        </Button>
      );
    }
  };

  return (
    <>
      <Modal
        dimmer="inverted"
        closeOnEscape={true}
        closeOnDimmerClick={true}
        closeIcon
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        open={open}
        trigger={renderModalTrigger()}
      >
        <Modal.Header>
          {'"'}
          {internship.title}
          {'" '}
          {approved ? t("applications.company.approved_applications") : t("applications.company.new_applications")}
        </Modal.Header>
        <Modal.Content>
          <Modal.Description>
            {applications.length > 0 ? (
              <StudentApplicationList
                applications={applications}
                handleConfirmation={handleApplicationConfirmation}
                handleCancellation={handleApplicationCancellation}
              />
            ) : (
              <span>{t("common.no_students_to_position")}.</span>
            )}
          </Modal.Description>
        </Modal.Content>
        <Modal.Actions></Modal.Actions>
      </Modal>
      <Confirm
        size="tiny"
        open={pendingApprovalId !== null}
        content={t("applications.notifications.approve_last_spot_confirm")}
        confirmButton={t("common.actions.confirm")}
        cancelButton={t("common.actions.cancel")}
        onCancel={() => setPendingApprovalId(null)}
        onConfirm={() => {
          if (pendingApprovalId !== null) doApprove(pendingApprovalId, true);
          setPendingApprovalId(null);
        }}
      />
    </>
  );
};

export default InternshipApplicationsModal;
